import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box, Paper, Typography, Button } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import { Link } from 'react-router-dom';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { currentUser, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        bgcolor="gray.50"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // If not logged in, redirect to login
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // If logged in but not admin, show 403 page
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <img 
              src="/milestone-logo.svg" 
              alt="Milestone" 
              className="h-12 w-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-medium text-[#0099da]">Briefcam Husky Calculator</h1>
          </div>

          {/* 403 Error */}
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <BlockIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h4" component="h2" gutterBottom color="error">
                403
              </Typography>
              <Typography variant="h6" gutterBottom>
                Access Denied
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                You don't have permission to access this page. Administrator privileges are required.
              </Typography>
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  component={Link}
                  to="/app"
                  variant="contained"
                  sx={{ 
                    bgcolor: '#0099da',
                    '&:hover': {
                      bgcolor: '#007bb3'
                    }
                  }}
                >
                  Go to Calculator
                </Button>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Signed in as: <strong>{currentUser.email}</strong>
              </Typography>
            </Box>
          </Paper>

          {/* Footer */}
          <div className="text-center mt-6">
            <Typography variant="body2" color="text.secondary">
              Need admin access? Contact your system administrator.
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  // If admin, render the children
  return <>{children}</>;
};

export default AdminRoute; 